import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { SuggestionProviderType } from "@/components/suggestions/types";
import type { TiptapSuggestionsRenderPlace } from "@/components/tiptap/tiptap-suggestions/TiptapSuggestions";

export type SuggestionsContextValue = {
  suggestionsAnchorId: string;
  suggestionsRenderPlace: TiptapSuggestionsRenderPlace;
  accept: SuggestionProviderType[];
};

export const SuggestionsContext = createContext<SuggestionsContextValue>({
  suggestionsAnchorId: "popover-root",
  suggestionsRenderPlace: "tiptap",
  accept: [],
});

export type SuggestionsProviderProps = {
  children: ReactNode;
  suggestionsAnchorId?: string;
  suggestionsRenderPlace?: TiptapSuggestionsRenderPlace;
  accept?: SuggestionProviderType[];
};

export function SuggestionsProvider({
  children,
  suggestionsAnchorId,
  suggestionsRenderPlace,
  accept,
}: SuggestionsProviderProps) {
  const parentValue = useContext(SuggestionsContext);

  const value: SuggestionsContextValue = useMemo(
    () => ({
      suggestionsAnchorId: suggestionsAnchorId ?? parentValue.suggestionsAnchorId,
      suggestionsRenderPlace: suggestionsRenderPlace ?? parentValue.suggestionsRenderPlace,
      accept: accept ?? parentValue.accept,
    }),
    [
      suggestionsAnchorId,
      suggestionsRenderPlace,
      accept,
      parentValue.suggestionsAnchorId,
      parentValue.suggestionsRenderPlace,
      parentValue.accept,
    ],
  );

  return <SuggestionsContext.Provider value={value}>{children}</SuggestionsContext.Provider>;
}

export function useSuggestionsContext() {
  return useContext(SuggestionsContext);
}
